// De microfoon naar het formaat dat Whisper verwacht: 16-bit mono WAV op 16 kHz.
//
// Waarom zelf en niet MediaRecorder: die levert webm/opus op een samplerate die
// per apparaat verschilt, en we willen per beurt precies het stuk meesturen dat
// de spraakdetectie heeft afgebakend — niet een heel opnamebestand. Bovendien
// is dit de enige plek waar de omvang van het verzoek te sturen is: 16 kHz mono
// is vier keer kleiner dan 48 kHz stereo, en dat scheelt merkbaar in de tijd
// tot het eerste geluid.
//
// Gaat hier iets mis, dan komt er geen fout maar ruis, en vraagt de interviewer
// eindeloos om herhaling.
//
// AI-CONTEXT: See docs/features/ai-interviewer-ovh-stt-tts.md

const TARGET_RATE = 16000;

/**
 * Rekent geluid terug naar 16 kHz.
 *
 * Lager dan 16 kHz laten we met rust: opwaarderen voegt geluid toe dat er niet
 * was, en Whisper heeft daar niets aan.
 *
 * @param {Float32Array} samples - Geluid zoals de microfoon het gaf
 * @param {number} sampleRate - Samplerate van dat geluid
 * @returns {Float32Array} Geluid op 16 kHz (of ongewijzigd als het al lager lag)
 */
const downsampleTo16k = (samples, sampleRate) => {
    if (sampleRate <= TARGET_RATE) return samples;

    const ratio = sampleRate / TARGET_RATE;
    const length = Math.round(samples.length / ratio);
    const out = new Float32Array(length);

    // Gemiddelde over het venster in plaats van elke n-de waarde pakken: los
    // pakken laat hoge tonen als valse lage tonen terugkomen (aliasing), en dat
    // klinkt voor Whisper als een ander woord.
    for (let i = 0; i < length; i++) {
        const start = Math.floor(i * ratio);
        const end = Math.min(Math.floor((i + 1) * ratio), samples.length);
        let sum = 0;
        let count = 0;
        for (let j = start; j < end; j++) {
            sum += samples[j];
            count += 1;
        }
        out[i] = count ? sum / count : 0;
    }
    return out;
};

/**
 * Bouwt een complete WAV uit ruw geluid.
 *
 * @param {Float32Array} samples - Geluid zoals de microfoon het gaf (-1 t/m 1)
 * @param {number} sampleRate - Samplerate van dat geluid
 * @returns {ArrayBuffer} Een WAV-bestand: 16-bit mono, 16 kHz
 */
const encodeWav = (samples, sampleRate) => {
    const audio = downsampleTo16k(samples, sampleRate);
    const rate = Math.min(sampleRate, TARGET_RATE);
    const buffer = new ArrayBuffer(44 + audio.length * 2);
    const view = new DataView(buffer);

    const writeText = (at, text) => {
        for (let i = 0; i < text.length; i++) view.setUint8(at + i, text.charCodeAt(i));
    };

    writeText(0, 'RIFF');
    view.setUint32(4, 36 + audio.length * 2, true);
    writeText(8, 'WAVE');
    writeText(12, 'fmt ');
    view.setUint32(16, 16, true);       // lengte van het fmt-blok
    view.setUint16(20, 1, true);        // 1 = ongecomprimeerd PCM
    view.setUint16(22, 1, true);        // mono
    view.setUint32(24, rate, true);
    view.setUint32(28, rate * 2, true); // bytes per seconde
    view.setUint16(32, 2, true);        // bytes per sample
    view.setUint16(34, 16, true);       // bits per sample
    writeText(36, 'data');
    view.setUint32(40, audio.length * 2, true);

    for (let i = 0; i < audio.length; i++) {
        // Begrenzen vóór het omrekenen: zonder dit klapt +1.5 om naar een groot
        // negatief getal en hoor je een luide tik precies waar iemand het
        // hardst praatte.
        const clamped = Math.max(-1, Math.min(1, audio[i]));
        view.setInt16(44 + i * 2, clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff, true);
    }

    return buffer;
};

export { downsampleTo16k, TARGET_RATE };
export default encodeWav;
